import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { toggle } from '@nullvoxpopuli/ember-composable-helpers';
import { type DetailedChangeset } from 'ember-changeset/types';
import set from 'ember-set-helper/helpers/set';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import { and, not } from 'ember-truth-helpers';
import DirectUploader from 'garaje/components/direct-uploader';
import FileUpload from 'garaje/components/file-upload';
import preventDefault from 'garaje/helpers/prevent-default';
import type FlashMessagesService from 'garaje/services/flash-messages';
import droppableUpload from 'garaje/utils/decorators/droppable-upload';

import type { PageModel } from '../page-editor';

interface SummaryPageImageBoxSignature {
  Args: {
    uploadImageUrl?: string;
    imgSrc?: string | null;
    isDisabled?: boolean;
    model: PageModel;
    changeset: DetailedChangeset<PageModel>;
    deleteImage(): void;
    setImage(): void;
    useDirectUploader?: boolean;
  };
}

@droppableUpload
class SummaryPageImageBox extends Component<SummaryPageImageBoxSignature> {
  declare flashMessages: FlashMessagesService;

  declare handleDragOver: (event: DragEvent) => void;
  declare handleDragEnter: (event: DragEvent) => void;
  declare handleDragLeave: (event: DragEvent) => void;
  declare handleDrop: (event: DragEvent) => void;
  declare isBeingDragged: boolean;
  declare files: File[] | FileList;

  imageBoxElement!: HTMLElement;

  validFileTypes = /image\/(gif|jpe?g|png)/;

  @tracked isImageUploading = false;
  @tracked directUploadUrl?: string;

  get _disableDragAndDrop(): boolean {
    return !!this.args.isDisabled;
  }

  onError = (): void => {
    this.flashMessages.showAndHideFlash('error', 'Image upload failed', {
      append: false,
      showAboveModalOverlay: true,
    });
  };

  selectFile = (): void => {
    this.imageBoxElement.querySelector('input')?.click();
  };

  <template>
    <div
      data-test-image-upload
      {{on "dragover" this.handleDragOver}}
      {{on "dragenter" this.handleDragEnter}}
      {{on "dragleave" this.handleDragLeave}}
      {{on "drop" this.handleDrop}}
      {{didInsert (set this "imageBoxElement")}}
      class="droppable flex {{if this.isBeingDragged 'drag-over'}}"
      ...attributes
    >
      {{#unless @useDirectUploader}}
        <div class="flex-none flex items-center mb-4 rounded-lg p-4" style="width:332px;height:232px">
          {{#if this.isImageUploading}}
            <div
              class="uploaderPlaceholder rounded-lg flex items-center"
              style="width:300px;height:200px"
              data-test-logo-preview
            >
              <svg class="circular mx-auto" style="zoom:200%">
                <circle
                  class="circular-path"
                  cx="50"
                  cy="50"
                  r="20"
                  fill="none"
                  stroke-width="1"
                  stroke-miterlimit="10"
                />
              </svg>
            </div>
          {{else if @model.image}}
            <img
              alt="Final Screen Preview"
              class="rounded-lg mx-auto"
              src={{@model.image}}
              style="max-width:300px;max-height:200px;"
              data-test-logo-preview
            />
          {{else}}
            <div
              data-test-img-placeholder
              class="uploaderPlaceholder rounded-lg flex items-center"
              style="width:300px;height:200px;"
            >
              <p class="text-center text-sm text-carbon-50 m-0" style="width:300px;">Final message image</p>
            </div>
          {{/if}}
        </div>
      {{/unless}}
      <div class="w-1/2 p-4 {{if @useDirectUploader 'flex items-start flex-1'}}">
        <div class="uploadButton block mb-4">
          {{#if @useDirectUploader}}
            <DirectUploader
              @currentUpload={{if @imgSrc (hash isValid=true readerResult=@imgSrc)}}
              @disabled={{@isDisabled}}
              @accept="image/*"
              @autoUpload={{true}}
              @directUploadURL={{this.directUploadUrl}}
              @label="Final message image"
              @subLabel=""
              @labelRetry="Use another photo"
              @onError={{this.onError}}
              @validSize={{5000000}}
              @validType={{this.validFileTypes}}
              @invalidTitle="Upload failed"
              @invalidBody="Photo must be a .jpg, .png, or .gif,<br>and smaller than 5 MB"
              @showResetButton={{false}}
              @onUploadComplete={{@setImage}}
              class="shadow-lg border-none !rounded-lg mr-8 w-[300px] h-50"
            />
          {{else}}
            <FileUpload
              @url={{@uploadImageUrl}}
              @namespace=""
              @param="file"
              @requestType="POST"
              @accept="image/png,image/gif,image/jpeg"
              @action={{@setImage}}
              @beginUpload={{toggle "isImageUploading" this}}
              @allUploaded={{toggle "isImageUploading" this}}
              @files={{this.files}}
              @validType={{this.validFileTypes}}
              @validSize={{5000000}}
              @invalidBody="Image must be smaller than 5 MB"
              @isDisabled={{@isDisabled}}
            />
          {{/if}}
        </div>

        <div class="flex flex-col">
          <div class="flex mb-4">
            <button
              class="btn btn--small btn--brand"
              type="button"
              disabled={{@isDisabled}}
              {{on "click" this.selectFile}}
            >Select file</button>

            {{#if (and @model.image (not @isDisabled))}}
              <a
                data-test-delete-image
                href="#"
                class="btn btn--small btn--hoverFill ml-2"
                {{on "click" (preventDefault @deleteImage)}}
                tabindex="-1"
              ><span class="icon-trash"></span> Delete</a>
            {{/if}}
          </div>

          <p class="text-sm flex items-center">
            <span>
              We recommend a 1400px &times; 950px image.
            </span>
            <img
              id="custom-final-image-guidelines"
              class="ml-2"
              width={{16}}
              height={{16}}
              src="/assets/images/gray-info.svg"
              alt="More info"
            />
          </p>
          <p class="text-sm"><strong>Tip:</strong>
            Upload a custom graphic with a greeting, campus map, or a special promotion for your visitors.</p>
        </div>
      </div>
    </div>

    <EmberPopover
      @targetId="custom-final-image-guidelines"
      @popperContainer="body"
      @side="right"
      @tooltipClass="rounded-lg"
      data-test-final-image-guidelines-popover
    >
      <div class="my-2 font-normal text-base">Custom final image guidelines</div>
      <ul class="my-2">
        <li class="my-1 text-sm">JPG, PNG or GIF <span class="text-grey">(required)</span></li>
        <li class="my-1 text-sm">5MB or less <span class="text-grey">(required)</span></li>
        <li class="my-1 text-sm">1400px &times; 950px image</li>
        <li class="my-1 text-sm">For animated GIFs, files with 30 frames or fewer</li>
      </ul>
    </EmberPopover>
  </template>
}

export default SummaryPageImageBox;
